import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import Icon from '@components/Icon'
import {
  trans,
  SLIDER_CONTROLS,
  PREVIOUS_SLIDE,
  NEXT_SLIDE,
  SLIDER_PROGRESS,
} from '@helpers/translator'
import useIsRTL from '@hooks/useIsRTL'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import '@splidejs/react-splide/css'
import type { Options } from '@splidejs/splide'
import { Intersection } from '@splidejs/splide-extension-intersection'

export type SwiperOptions = Options

interface SwiperProps {
  slides: React.ReactNode[]
  options?: Options
  paginationClassnames?: string
  className?: string
  onIndexChange?: (index: number) => void
}

const Swiper: React.FC<SwiperProps> = ({
  slides,
  options = {},
  paginationClassnames = '',
  className = '',
  onIndexChange,
}) => {
  const splideRef = useRef<Splide | null>(null)
  const isRTL = useIsRTL()
  const [currentProgress, setCurrentProgress] = useState(0)

  const defaultOptions = useMemo(() => {
    const baseOptions = {
      type: 'loop',
      rewind: true,
      perPage: 1,
      perMove: 1,
      pagination: {
        type: 'bullets',
        el: 'splide__pagination',
      },
      arrows: true,
      interval: 5000,
      gap: '40px',
      mediaQuery: 'min',
      direction: isRTL ? 'rtl' : 'ltr',
      breakpoints: {
        1024: {
          pagination: false,
        },
      },
    }

    if (options.autoplay !== false) {
      return {
        ...baseOptions,
        autoplay: 'pause',
        intersection: {
          inView: {
            autoplay: true,
          },
          outView: {
            autoplay: false,
          },
        },
      }
    }

    return baseOptions
  }, [isRTL, options.autoplay])

  const mergedOptions = useMemo(
    () => ({
      ...defaultOptions,
      ...options,
      breakpoints: options.breakpoints
        ? Object.keys(options.breakpoints).reduce(
            (acc, breakpoint) => ({
              ...acc,
              [Number(breakpoint)]: {
                ...defaultOptions.breakpoints?.[
                  Number(breakpoint) as keyof typeof defaultOptions.breakpoints
                ],
                ...options.breakpoints?.[
                  Number(breakpoint) as keyof typeof options.breakpoints
                ],
              },
            }),
            { ...defaultOptions.breakpoints },
          )
        : defaultOptions.breakpoints,
    }),
    [defaultOptions, options],
  )

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!splideRef.current) return

      const splide = splideRef.current.splide

      if (!splide) return

      const progress = e.currentTarget
      const rect = progress.getBoundingClientRect()
      const clickedPosition = e.clientX - rect.left
      const progressWidth = rect.width
      const clickedRate = clickedPosition / progressWidth
      const end = splide.Components.Controller.getEnd() + 1
      const targetIndex = Math.floor(clickedRate * end)
      splide.go(targetIndex)
    },
    [],
  )

  useEffect(() => {
    const splide = splideRef.current?.splide
    if (!splide) return

    const updateProgress = () => {
      const end = splide.Components.Controller.getEnd() + 1
      const rate = Math.min((splide.index + 1) / end, 1)
      setCurrentProgress(Math.round(rate * 100))
    }

    splide.on('mounted move', updateProgress)
    updateProgress()

    if (onIndexChange) {
      splide.on('move', onIndexChange)
    }

    return () => {
      splide.off('mounted move', updateProgress)

      if (onIndexChange) {
        splide.off('move', onIndexChange)
      }
    }
  }, [])

  return (
    <Splide
      hasTrack={false}
      extensions={{ Intersection }}
      ref={splideRef}
      options={mergedOptions}
      className={className}
    >
      <div className="flex flex-col">
        <SplideTrack>
          {slides.map((slide, index) => (
            <SplideSlide key={`slide-${index}`}>{slide}</SplideSlide>
          ))}
        </SplideTrack>

        <div
          className={twMerge(
            `flex items-center mt-8 lg:mt-9 lg:ltr:ml-11 lg:ltr:mr-7 lg:rtl:ml-7 lg:rtl:mr-11`,
            paginationClassnames,
          )}
        >
          <ul className="splide__pagination"></ul>
          <SliderArrows />
          <ProgressBar
            className="hidden lg:flex"
            progress={currentProgress}
            onClick={handleProgressClick}
          />
        </div>
      </div>
    </Splide>
  )
}

const SliderArrows: React.FC = () => (
  <div
    className="splide__arrows flex gap-x-3"
    role="group"
    aria-label={trans(SLIDER_CONTROLS)}
  >
    <button
      className="splide__arrow--prev w-[28px] h-[28px] bg-black rounded-full flex items-center justify-center"
      aria-label={trans(PREVIOUS_SLIDE)}
      type="button"
    >
      <Icon
        name="arrow"
        size="auto"
        aria-hidden="true"
        className="text-white"
      />
    </button>
    <button
      className="splide__arrow--next w-[28px] h-[28px] bg-black rounded-full flex items-center justify-center"
      aria-label={trans(NEXT_SLIDE)}
      type="button"
    >
      <Icon
        name="arrow"
        size="auto"
        aria-hidden="true"
        className="text-white"
      />
    </button>
  </div>
)

interface ProgressBarProps {
  progress: number
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void
  className?: string
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  onClick,
  className = '',
}) => (
  <div
    onClick={onClick}
    className={twMerge(
      'slide__progress flex-1 h-0.5 bg-gray-300 cursor-pointer ltr:ml-4 rtl:mr-4 select-none',
      className,
    )}
    role="slider"
    aria-label={trans(SLIDER_PROGRESS)}
    tabIndex={0}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={progress}
  >
    <div
      className="slide__progress-bar h-full bg-black transition-width duration-300 ease-in-out"
      style={{ width: `${progress}%` }}
    />
  </div>
)

export default Swiper
